import React from 'react';
import './Dashboard.css';
import { useDashboard } from '../hooks/useDashboard';
import DashboardHeader from './Dashboard/DashboardHeader';
import DashboardControls from './Dashboard/DashboardControls';
import EmployeeTable from './Dashboard/EmployeeTable';
import Pagination from './Dashboard/Pagination';
import EditGoodieModal from './Dashboard/EditGoodieModal';
import StatisticsModal from './Dashboard/StatisticsModal';

function Dashboard() {
  const {
    // State
    loading,
    error,
    searchTerm,
    selectedLocation,
    currentPage,
    itemsPerPage,
    isModalOpen,
    selectedEmployee,
    modalGoodieStatuses,
    modalGoodieSizes,
    modalSizeChangeReasons,
    editingSizes,
    savingSizes,
    markingCompleted,
    isStatsModalOpen,
    employees,
    goodies,
    filteredEmployees,
    
    // Computed values
    goodieTypes,
    locations,
    stats,
    totalPages,
    currentEmployees,
    
    // Setters
    setSearchTerm,
    setSelectedLocation,
    setIsStatsModalOpen,
    
    // Handlers
    fetchEmployees,
    clearFilters,
    exportToExcel,
    openModal,
    closeModal,
    handleModalStatusChange,
    handleModalSizeChange,
    handleSizeChangeReasonChange,
    startEditingSize,
    cancelEditingSize,
    saveSizeChange,
    markGoodieAsCollected,
    saveModalChanges,
    handlePageChange,
    handleItemsPerPageChange
  } = useDashboard();

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="loading">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-page">
        <div className="error">Error: {error}</div>
        <button onClick={fetchEmployees} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <DashboardHeader onStatsClick={() => setIsStatsModalOpen(true)} />

      <div className="dashboard-content">
        {/* Page Title */}
        <h2 className="page-title">Goodies Distribution</h2>
        
        {/* Unified Data Management Container */}
        <div className="data-management-container">
          <DashboardControls
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedLocation={selectedLocation}
            setSelectedLocation={setSelectedLocation}
            locations={locations}
            onExport={exportToExcel}
            onClearFilters={clearFilters}
            filteredEmployeesCount={filteredEmployees.length}
          />

          <EmployeeTable
            employees={currentEmployees}
            goodieTypes={goodieTypes}
            goodies={goodies}
            onViewModify={openModal}
          />

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            totalItems={filteredEmployees.length}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </div>

        {/* No Results Section - Outside the unified container */}
        {filteredEmployees.length === 0 && (
          <div className="no-results">
            <p>No employees found matching your criteria.</p>
            {(searchTerm || selectedLocation) && (
              <button onClick={clearFilters} className="clear-filters-button">
                Clear Filters
              </button>
            )}
          </div>
        )}
      </div>

      <EditGoodieModal
        isOpen={isModalOpen}
        selectedEmployee={selectedEmployee}
        goodieTypes={goodieTypes}
        goodies={goodies}
        modalGoodieStatuses={modalGoodieStatuses}
        modalGoodieSizes={modalGoodieSizes}
        modalSizeChangeReasons={modalSizeChangeReasons}
        editingSizes={editingSizes}
        savingSizes={savingSizes}
        markingCompleted={markingCompleted}
        onClose={closeModal}
        onStatusChange={handleModalStatusChange}
        onSizeChange={handleModalSizeChange}
        onSizeChangeReasonChange={handleSizeChangeReasonChange}
        onStartEditingSize={startEditingSize}
        onCancelEditingSize={cancelEditingSize}
        onSaveSizeChange={saveSizeChange}
        onMarkAsCollected={markGoodieAsCollected}
        onSaveChanges={saveModalChanges}
      />

      <StatisticsModal
        isOpen={isStatsModalOpen}
        onClose={() => setIsStatsModalOpen(false)}
        goodieTypes={goodieTypes}
        stats={stats}
        employees={employees}
      />
    </div>
  );
}

export default Dashboard;
